import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AutoComplete,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Input,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlayCircleOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { startRun } from '@/api/runs';
import { listEntities } from '@/api/entities';
import { ulid } from '@/api/ulid';
import { entityIdProblem, freshRunId, parseRunInput } from './startRunModel';
import { normalizeApiError, type NormalizedApiError } from '@/api/errors';
import type { RunStartResult } from '@/api/types';
import { getPermissionMatrix } from '@/admin/permissionMatrix';

const { Text, Paragraph } = Typography;

export interface StartRunDrawerProps {
  open: boolean;
  flowId: string;
  flowName?: string;
  /** The published version to run — the drawer runs the signed artifact only. */
  publishedVersion: number | null;
  onClose: () => void;
  /** Fired on 202 — the caller may refresh its own state; navigation stays the drawer's. */
  onStarted?: (result: RunStartResult) => void;
}

function rolesFor(key: string): string[] {
  try {
    return getPermissionMatrix().keys.find((k) => k.key === key)?.roles ?? [];
  } catch {
    return [];
  }
}

/**
 * The console's "Start run" (OPSFL-FLOWOPS-1 A1): the affordance the product never had over the
 * RATIFIED `POST /v1/runs`. Collects the optional subject entity (ULID — picker over the hub's
 * entity ids), optional input JSON (advanced), shows the generated run id, and on 202 offers the
 * deep link to the run. Runs execute the PUBLISHED version only — the server refuses drafts and so
 * does this drawer. Errors are shown verbatim (code + message), with the permission named on 403.
 */
export function StartRunDrawer(props: StartRunDrawerProps) {
  const { open, flowId, flowName, publishedVersion, onClose, onStarted } = props;
  const navigate = useNavigate();

  const [runId, setRunId] = useState<string>(() => freshRunId());
  const [idemKey, setIdemKey] = useState<string>(() => ulid());
  const [entityId, setEntityId] = useState('');
  const [entityOptions, setEntityOptions] = useState<{ value: string; label: string }[]>([]);
  const [inputText, setInputText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<NormalizedApiError | null>(null);
  const [result, setResult] = useState<RunStartResult | null>(null);

  const reset = useCallback(() => {
    setRunId(freshRunId());
    setIdemKey(ulid());
    setEntityId('');
    setEntityOptions([]);
    setInputText('');
    setBusy(false);
    setErr(null);
    setResult(null);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const regenerate = useCallback(() => {
    // A new run id is a NEW logical start — the idempotency key moves with it.
    setRunId(freshRunId());
    setIdemKey(ulid());
    setErr(null);
  }, []);

  const entityProblem = entityIdProblem(entityId);
  const inputParsed = useMemo(() => parseRunInput(inputText), [inputText]);
  const canStart =
    publishedVersion != null && !busy && !entityProblem && inputParsed.ok && runId.trim().length > 0;

  const searchEntities = useCallback(async (q: string) => {
    const prefix = q.trim().toUpperCase();
    if (prefix.length < 2) {
      setEntityOptions([]);
      return;
    }
    try {
      const page = await listEntities({ idPrefix: prefix, pageSize: 8 });
      setEntityOptions(page.items.map((e) => ({ value: e.id, label: `${e.id} · ${e.typeKey}` })));
    } catch {
      // The picker is a convenience over the entity browser; a failed lookup never blocks a
      // typed ULID (the field validates the shape locally).
      setEntityOptions([]);
    }
  }, []);

  const doStart = useCallback(async () => {
    if (!canStart || publishedVersion == null) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await startRun(
        {
          flowId,
          version: publishedVersion,
          runId: runId.trim(),
          entityId: entityId.trim() || undefined,
          input: inputParsed.ok ? inputParsed.value : undefined,
        },
        idemKey,
      );
      setResult(res);
      onStarted?.(res);
    } catch (e) {
      setErr(normalizeApiError(e));
    } finally {
      setBusy(false);
    }
  }, [canStart, publishedVersion, flowId, runId, entityId, inputParsed, idemKey, onStarted]);

  const openRun = useCallback(() => {
    if (!result) return;
    onClose();
    navigate(`/runs?runId=${encodeURIComponent(result.runId)}`);
  }, [result, onClose, navigate]);

  const errBanner = () => {
    if (!err) return null;
    if (err.httpStatus === 403) {
      const roles = rolesFor('runs.start');
      return (
        <Alert
          type="warning"
          showIcon
          message="You cannot start runs"
          description={
            <span>
              Starting a run needs <Text className="ol-mono">runs.start</Text>
              {roles.length ? ` (${roles.join(', ')})` : ''}. Server said: {err.code} — {err.message}
            </span>
          }
        />
      );
    }
    if (err.code === 'IDEMPOTENCY_KEY_CONFLICT') {
      return (
        <Alert
          type="error"
          showIcon
          message="Idempotency conflict"
          description={`${err.message} — regenerate the run id to start a new run.`}
        />
      );
    }
    if (err.code === 'FEATURE_DISABLED') {
      return (
        <Alert
          type="info"
          showIcon
          message="Flow runtime is not enabled in this deployment"
          description={`${err.code} — ${err.message}`}
        />
      );
    }
    return <Alert type="error" showIcon message={err.code} description={err.message} />;
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      title={
        <Space>
          <PlayCircleOutlined />
          Start run · {flowName ?? flowId}
        </Space>
      }
      styles={{ body: { padding: 16 } }}
      extra={
        <Button
          size="small"
          type="link"
          onClick={() => {
            onClose();
            navigate(`/bulk?flowId=${encodeURIComponent(flowId)}`);
          }}
        >
          Start many from a CSV
        </Button>
      }
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="success"
            showIcon
            message="Run started"
            description={
              <div>
                <div>
                  <Text className="ol-mono" style={{ fontSize: 12 }}>
                    {result.runId}
                  </Text>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ol-text-tertiary)' }} className="ol-mono">
                  {result.workflowId}
                </div>
              </div>
            }
          />
          <Space>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={openRun}>
              Open run
            </Button>
            <Button onClick={reset}>Start another</Button>
          </Space>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            The run executes the signed v{publishedVersion} artifact. It appears on the Runs page the
            moment the runtime records it; the drawer there shows node-by-node state.
          </Paragraph>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {publishedVersion == null && (
            <Alert
              type="warning"
              showIcon
              message="Nothing published to run"
              description="Runs execute the signed, published version only. Publish this flow first — the publish drawer ends with 'Run it now'."
            />
          )}
          <Descriptions size="small" column={1} labelStyle={{ width: 110 }}>
            <Descriptions.Item label="Flow">
              <Space size={6}>
                <Text className="ol-mono">{flowId}</Text>
                {publishedVersion != null && (
                  <Tag color="success" bordered={false}>
                    published v{publishedVersion}
                  </Tag>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Run id">
              <Space size={6}>
                <Text className="ol-mono" style={{ fontSize: 12 }} data-testid="start-run-id">
                  {runId}
                </Text>
                <Tooltip title="Generate a new run id (a new logical start)">
                  <Button
                    size="small"
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={regenerate}
                    aria-label="Regenerate run id"
                  />
                </Tooltip>
              </Space>
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Text strong style={{ fontSize: 12 }}>
              Subject entity (optional)
            </Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 4 }}
              value={entityId}
              options={entityOptions}
              onSearch={(v) => void searchEntities(v)}
              onChange={(v) => setEntityId(typeof v === 'string' ? v : '')}
              placeholder="Entity ULID — type to search this hub's entities"
              status={entityProblem ? 'error' : undefined}
              aria-label="Subject entity id"
              data-testid="start-run-entity"
            />
            {entityProblem ? (
              <Text type="danger" style={{ fontSize: 11.5 }}>
                {entityProblem}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                Binds the run to a case subject: evidence pins to it and the run shows on its
                entity-360. Leave blank for a subject-less run.
              </Text>
            )}
          </div>

          <Collapse
            size="small"
            ghost
            items={[
              {
                key: 'input',
                label: 'Advanced — run input (JSON)',
                children: (
                  <div>
                    <Input.TextArea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                      placeholder='{"channel": "branch"}'
                      className="ol-mono"
                      style={{ fontSize: 12 }}
                      status={inputParsed.ok ? undefined : 'error'}
                      aria-label="Run input JSON"
                    />
                    <Text type={inputParsed.ok ? 'secondary' : 'danger'} style={{ fontSize: 11.5 }}>
                      {inputParsed.ok
                        ? 'Exposed to CEL guards and decisions as `input`. Optional.'
                        : inputParsed.error}
                    </Text>
                  </div>
                ),
              },
            ]}
          />

          {errBanner()}

          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={busy}
              disabled={!canStart}
              onClick={() => void doStart()}
            >
              Start run
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            POST /v1/runs with an idempotency key — retrying this exact start never double-runs.
          </Text>
        </Space>
      )}
    </Drawer>
  );
}
